import { useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ContentLayout } from '@/bundle/Layouts/ContentLayout/ContentLayout';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import { OrganizationUserPersonalDetails } from './ui/OrganizationUserPersonalDetails/OrganizationUserPersonalDetails';
import { OrganizationUserAccountDetails } from './ui/OrganizationUserAccountDetails/OrganizationUserAccountDetails';
import { Box } from '@/components/Box/Box';
import { getOpcoOrganizationUsersPath } from '../path/path';
import { UserDetailsHeader } from '@/bundle/shared/components/UserDetails/UserDetailsHeader/UserDetailsHeader';
import LogoSmallIcon from '@/bundle/_Opco/_OpcoUsersPage/OpcoUserDetailsPage/images/LogoSmall.svg?react';
import { InviteNotification } from '@/bundle/_Opco/ui/OrganizationDetails/Details/ui/OrganizationUserAccountDetails/styles';
import { Notification } from '@/components/Notification/Notification';
import { NotificationMessage, NotificationTitle } from '@/components/Notification/styles';
import { RESEND_INVITATION_NOTIFICATION } from '@/const/shared';
import { isInvitedOrganizationUser } from '@/helpers/organizationHelpers';
import { loadOrganizationUser } from '@/bundle/_Opco/ui/OrganizationDetails/Details/api/api';
import { useStatefulNavigation } from '@/hooks/useStatefulNavigation';

export const OrganizationUserDetailsPage = () => {
  const { navigateBack } = useStatefulNavigation();
  const { organizationId, userId } = useParams<{ organizationId: string; userId: string }>();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['load_organization_user', organizationId, userId],
    queryFn: () => loadOrganizationUser(organizationId, userId),
  });

  const refetchUser = () => {
    queryClient.invalidateQueries({ queryKey: ['load_organization_user', organizationId, userId] });
  };

  const navigateToOrganizationUsers = () => {
    navigateBack(getOpcoOrganizationUsersPath(organizationId));
  };

  const organizationUser = data?.body;
  const isInvited = isInvitedOrganizationUser(organizationUser?.status);

  return (
    <ContentLayout
      title='Organization User Details'
      breadcrumbs='Organizations > Details > User Details'
      backButton='Back to Organization Users'
      onBack={navigateToOrganizationUsers}
    >
      <PageBody>
        <Box>
          <UserDetailsHeader
            title='WireVault Account Information'
            icon={<LogoSmallIcon />}
            subTitle='You must enter your PIN code to edit these fields.'
          />
          <Box
            width={isInvited ? '100%' : '50%'}
            display='flex'
            flexDirection='row'
            columnGap='28px'
            mobile={{ width: '100%', flexDirection: 'column-reverse', rowGap: '42px' }}
          >
            {organizationUser && (
              <Box width='100%' display='flex' flexDirection='column' rowGap='58px' mobile={{ rowGap: '42px' }}>
                <OrganizationUserAccountDetails user={organizationUser} onRefetchUser={refetchUser} />
                <OrganizationUserPersonalDetails user={organizationUser} onSave={refetchUser} />
              </Box>
            )}
            {isInvited && (
              <Box width='100%'>
                <InviteNotification>
                  <Notification>
                    <NotificationTitle>The invitation to WireVault has not been accepted.</NotificationTitle>
                    <NotificationMessage>
                      Please be sure the email address and mobile phone number are correct. You can change the
                      information for this account until the invitation is accepted.
                      <Box marginTop='12px'>{RESEND_INVITATION_NOTIFICATION}</Box>
                    </NotificationMessage>
                  </Notification>
                </InviteNotification>
              </Box>
            )}
          </Box>
        </Box>
      </PageBody>
    </ContentLayout>
  );
};
